import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as amplitude from "@amplitude/analytics-browser";
import {
  Breadcrumbs,
  Toggle,
  Checkbox,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  FormatCurrencyForDisplay,
  Button,
  SkeletonBase,
  Input,
} from '@unified-checkout/design-system';
import {
  CircleInfo,
  PaintbrushPencil,
} from '@unified-checkout/design-system/icons/index';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { DeactivateConfirmModal } from '@app/dashboard/components/inner_layout/PayMeCard/DeactivateConfirmModal';
import {
  changePaymentHandleStatus,
  updatePaymentHandleSettings,
} from '@app/dashboard/services/inner_layout/payme';
import { usePaymentHandleConfig } from '@app/dashboard/utils/paymentHandleConfig';
import {
  getIntegrationsNext,
  getIntegrationsCurrencies,
} from '@app/dashboard/services/inner_layout/integrations';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { PROFILE } from '@app/dashboard/constants/profile';
import { PAYME_BASE_URL } from '@app/dashboard/constants/payme-links';
import { PAYME_EVENTS } from '@app/dashboard/constants/tracking_events/payme';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { getProfileType } from '@app/dashboard/utils/localStorageUtils';
import { notify } from '@app/dashboard/utils/notifier';
import { PaymentMethodsList } from './PaymentMethodsList';
import { getPayMeErrorMessage } from '../utils/errorMessage';
import type { IIntegrationsNextResponse } from '@app/dashboard/types/innerLayout/payment_integrations/get-integrations';
import styles from './settings.module.scss';

export const PayMeSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isStaff = getProfileType() === PROFILE.TYPE.MERCHANT_STAFF;
  const isMerchant = getProfileType() === PROFILE.TYPE.MERCHANT;
  const canChangePaymentHandle = isStaff ? isAccessible(PERMISSIONS.CAN_CHANGE_PAYMENT_HANDLE) : isMerchant;
  const canViewPaymentIntegrations = isStaff ? isAccessible(PERMISSIONS.CAN_VIEW_PAYMENT_INTEGRATIONS) : isMerchant;

  const [isActive, setIsActive] = useState(true);
  const [handle, setHandle] = useState('');
  const [mandatoryInfo, setMandatoryInfo] = useState<
    'mobile' | 'email' | 'both'
  >('both');
  const [collectCustomerName, setCollectCustomerName] = useState(false);
  const [collectNotes, setCollectNotes] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const [paymentMethodsList, setPaymentMethodsList] = useState<number[]>([]);
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<
    Map<string, number>
  >(new Map());
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    Map<
      string,
      Array<{ gateway_type: string; id: number; integration_name?: string }>
    >
  >(new Map());
  // Store original settings to revert on error
  const [originalSettings, setOriginalSettings] = useState<{
    mandatoryInfo: 'mobile' | 'email' | 'both';
    collectCustomerName: boolean;
    collectNotes: boolean;
    selectedCurrency: string;
    paymentMethodsList: number[];
  } | null>(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  // Track toggle version to force re-render on cancel
  const [toggleVersion, setToggleVersion] = useState(0);

  const queryClient = useQueryClient();
  // Track if toggle has been initialized to prevent initial onChange events
  const toggleInitializedRef = useRef(false);
  // Track previous currency to detect actual currency changes
  const previousCurrencyRef = useRef<string>('');
  // Track if payment methods have been initialized from API to prevent overwriting user selections
  const paymentMethodsInitializedRef = useRef(false);
  // Track if currency has been initialized from API to prevent default currency from overriding it
  const currencyInitializedFromApiRef = useRef(false);

  const { isLive } = useTestLiveModeStore();

  const {
    data: currencies,
    isError: isIntegrationsError,
    isLoading: isIntegrationsLoading,
  } = useQuery<string[]>({
    queryKey: [QUERY_KEYS.GET_INTEGRATIONS_CURRENCIES, isLive],
    queryFn: () => getIntegrationsCurrencies({ isLive }).then((res) => res.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: canViewPaymentIntegrations,
  });

  const {
    data: paymentHandleConfig,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = usePaymentHandleConfig();

  const {
    data: integrationsNextData,
    isLoading: isIntegrationsNextLoading,
    isError: isIntegrationsNextError,
  } = useQuery<IIntegrationsNextResponse>({
    queryKey: ['getIntegrationsNext', selectedCurrency, isLive],
    queryFn: () =>
      getIntegrationsNext({
        currency: selectedCurrency,
        isLive,
      }).then((res) => res.data),
    enabled: !!selectedCurrency && canViewPaymentIntegrations,
  });

  // Extract available payment methods
  useEffect(() => {
    if (!canViewPaymentIntegrations) {
      return;
    }
    if (integrationsNextData && selectedCurrency) {
      const methodsMap = new Map<
        string,
        Array<{ gateway_type: string; id: number; integration_name?: string }>
      >();

      // Transform API response to match expected format for payment methods list
      Object.keys(integrationsNextData).forEach((gatewayType) => {
        const integrations = integrationsNextData[gatewayType];
        methodsMap.set(
          gatewayType,
          integrations.map((integration) => ({
            gateway_type: integration.gateway_type_name,
            id: integration.id,
            integration_name: integration.integration_name || undefined,
          }))
        );
      });

      setAvailablePaymentMethods(methodsMap);

      // Only restore payment methods when currency actually changes (not on other dependency updates)
      const currencyChanged = previousCurrencyRef.current !== selectedCurrency;
      previousCurrencyRef.current = selectedCurrency;

      // If switching back to original currency, restore original payment methods
      if (
        currencyChanged &&
        originalSettings &&
        selectedCurrency === originalSettings.selectedCurrency &&
        originalSettings.paymentMethodsList.length > 0
      ) {
        // Collect all valid integration IDs for the current currency
        const validIntegrationIds = new Set<number>();
        methodsMap.forEach((integrations) => {
          integrations.forEach((integration) => {
            validIntegrationIds.add(integration.id);
          });
        });

        // Restore original payment methods that are still valid in this currency
        const restoredMethods = originalSettings.paymentMethodsList.filter(
          (id) => validIntegrationIds.has(id)
        );
        setPaymentMethodsList(restoredMethods);

        // Restore selected integration IDs based on original payment methods
        const restoredIds = new Map<string, number>();
        restoredMethods.forEach((integrationId) => {
          // Find which gateway type this integration ID belongs to in the current currency
          methodsMap.forEach((integrations, gatewayType) => {
            const matchingIntegration = integrations.find(
              (integration) => integration.id === integrationId
            );
            if (matchingIntegration) {
              restoredIds.set(gatewayType, integrationId);
            }
          });
        });
        setSelectedIntegrationIds(restoredIds);
      } else {
        // This runs when currency didn't change OR when currency changed to a different currency
        setSelectedIntegrationIds((prev) => {
          const updated = new Map<string, number>();
          prev.forEach((integrationId, gatewayType) => {
            const integrations = methodsMap.get(gatewayType);
            // Only keep the selection if the gateway type exists in the new currency
            // and the integration ID is still valid
            if (
              integrations &&
              integrations.some((item) => item.id === integrationId)
            ) {
              updated.set(gatewayType, integrationId);
            }
          });
          return updated;
        });
      }
    } else {
      setAvailablePaymentMethods(new Map());
      setSelectedIntegrationIds(new Map());
    }
  }, [
    canViewPaymentIntegrations,
    integrationsNextData,
    selectedCurrency,
    originalSettings,
  ]);

  // Restore selected integration IDs when payment config and integrations data are both available
  // Only run once on initial load to prevent overwriting user selections
  useEffect(() => {
    if (!canViewPaymentIntegrations) {
      return;
    }
    if (
      !paymentMethodsInitializedRef.current &&
      paymentHandleConfig?.payment_methods_list &&
      paymentHandleConfig.payment_methods_list.length > 0 &&
      availablePaymentMethods.size > 0
    ) {
      const methodsList = paymentHandleConfig.payment_methods_list.map((m) =>
        typeof m === 'number' ? m : Number(m)
      );
      const restoredIds = new Map<string, number>();

      // Map integration IDs to their gateway types
      // Since integration IDs are unique across currencies, we can directly map them
      methodsList.forEach((integrationId) => {
        availablePaymentMethods.forEach((integrations, gatewayType) => {
          const matchingIntegration = integrations.find(
            (integration) => integration.id === integrationId
          );
          if (matchingIntegration) {
            restoredIds.set(gatewayType, integrationId);
          }
        });
      });

      setSelectedIntegrationIds(restoredIds);
      paymentMethodsInitializedRef.current = true;
    }
  }, [
    canViewPaymentIntegrations,
    paymentHandleConfig,
    availablePaymentMethods,
  ]);

  // Initialize form fields from fetched configuration
  useEffect(() => {
    if (paymentHandleConfig) {
      // Set handle from API response
      setHandle(paymentHandleConfig.handle_name);

      // Set active status
      setIsActive(paymentHandleConfig.is_activated);

      // Set mandatory info based on API data
      if (
        paymentHandleConfig.is_email_mandatory &&
        paymentHandleConfig.is_phone_mandatory
      ) {
        setMandatoryInfo('both');
      } else if (paymentHandleConfig.is_email_mandatory) {
        setMandatoryInfo('email');
      } else {
        setMandatoryInfo('mobile');
      }

      // Set checkboxes
      setCollectCustomerName(paymentHandleConfig.is_collect_customer_name);
      setCollectNotes(paymentHandleConfig.is_collect_payment_purpose);

      // Set currency from API response (prioritize API currency over default)
      if (paymentHandleConfig.currency) {
        setSelectedCurrency(paymentHandleConfig.currency);
        currencyInitializedFromApiRef.current = true;
      }

      // Set payment methods list (API returns integration IDs as numbers or strings)
      const methodsList =
        paymentHandleConfig.payment_methods_list?.map((m) =>
          typeof m === 'number' ? m : Number(m)
        ) || [];
      setPaymentMethodsList(methodsList);

      // Store original settings for error reversion
      setOriginalSettings({
        mandatoryInfo:
          paymentHandleConfig.is_email_mandatory &&
          paymentHandleConfig.is_phone_mandatory
            ? 'both'
            : paymentHandleConfig.is_email_mandatory
            ? 'email'
            : 'mobile',
        collectCustomerName: paymentHandleConfig.is_collect_customer_name,
        collectNotes: paymentHandleConfig.is_collect_payment_purpose,
        selectedCurrency: paymentHandleConfig.currency || '',
        paymentMethodsList: methodsList,
      });

      // Mark toggle as initialized after data is loaded
      requestAnimationFrame(() => {
        toggleInitializedRef.current = true;
      });
    }
  }, [paymentHandleConfig]);

  // Mark toggle as initialized after component mounts
  useEffect(() => {
    requestAnimationFrame(() => {
      toggleInitializedRef.current = true;
    });
  }, []);

  // Change payment handle status mutation
  const changeStatusMutation = useMutation({
    mutationFn: ({
      handle_name,
      activate,
    }: {
      handle_name: string;
      activate: boolean;
    }) => {
      return changePaymentHandleStatus({ handle_name, activate });
    },
    onSuccess: (_, variables) => {
      // Invalidate queries to refetch data (backend will handle branch deactivation)
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRANCH_HANDLES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
      });

      const statusText = variables.activate
        ? t('innerLayout.paymePage.settings.linkStatus.activated')
        : t('innerLayout.paymePage.settings.linkStatus.deactivated');

      notify.success(t('innerLayout.paymePage.settings.linkStatus.updateSuccess', { status: statusText }), { maxWidth: '32rem' });

      if (variables.activate === true) {
        amplitude.track(PAYME_EVENTS.PAYME_ACTIVATION_ENABLED_SUCCEEDED);
      } else if (variables.activate === false) {
        amplitude.track(PAYME_EVENTS.PAYME_ACTIVATION_DISABLED_SUCCEEDED);
      }
    },
    onError: (error: any, variables) => {
      // Revert the toggle state on error
      setIsActive(!isActive);

      const errorMessage = getPayMeErrorMessage(error, t);

      notify.error(errorMessage, { maxWidth: "32rem" });

      if (variables.activate === true) {
        amplitude.track(PAYME_EVENTS.PAYME_ACTIVATION_ENABLED_FAILED, {
          error_description: errorMessage,
        });
      } else if (variables.activate === false) {
        amplitude.track(PAYME_EVENTS.PAYME_ACTIVATION_DISABLED_FAILED, {
          error_description: errorMessage,
        });
      }
    },
  });

  const handleToggleChange = (checked: boolean) => {
    // Only update if toggle has been initialized (prevent initial onChange events)
    if (!toggleInitializedRef.current) {
      return;
    }

    // Only update if the value actually changed
    if (isActive === checked) {
      return; // No change, skip API call
    }

    if (!handle) return;

    // If deactivating (going from true to false), show confirmation modal
    if (isActive && !checked) {
      setShowDeactivateConfirm(true);
      return;
    }

    // If activating, proceed directly
    setIsActive(checked);
    changeStatusMutation.mutate({
      handle_name: handle,
      activate: checked,
    });
  };

  const handleConfirmDeactivate = () => {
    if (!handle) return;
    setIsActive(false);
    changeStatusMutation.mutate({
      handle_name: handle,
      activate: false,
    });
    setShowDeactivateConfirm(false);
  };

  const handleCancelDeactivate = () => {
    setShowDeactivateConfirm(false);
    // Force toggle to revert by incrementing version key
    setToggleVersion((prev) => prev + 1);
  };

  // Get default currency
  const getDefaultCurrency = () => {
    const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);
    const countryCurrency = COUNTRIES_INFO[country]?.currency;

    const currency = currencies?.includes(countryCurrency)
      ? countryCurrency
      : currencies?.[0];

    return currency || '';
  };

  // Only set default currency if paymentHandleConfig doesn't have a currency
  useEffect(() => {
    if (
      !canViewPaymentIntegrations ||
      isIntegrationsError ||
      isIntegrationsLoading ||
      !currencies ||
      currencies.length === 0 ||
      isConfigLoading ||
      currencyInitializedFromApiRef.current
    ) {
      return;
    }
    // Only set default currency if paymentHandleConfig doesn't exist or doesn't have a currency
    if (!paymentHandleConfig || !paymentHandleConfig.currency) {
      const defaultCurrency = getDefaultCurrency();
      if (defaultCurrency) {
        setSelectedCurrency(defaultCurrency);
      }
    }
  }, [
    canViewPaymentIntegrations,
    currencies,
    isIntegrationsError,
    isIntegrationsLoading,
    isConfigLoading,
    paymentHandleConfig,
  ]);

  // Localize currency
  const localizeCurrency = (currency?: string) => {
    if (
      currency &&
      !t(`innerLayout.currencies.${currency}`).includes('innerLayout')
    ) {
      return t(`innerLayout.currencies.${currency}`);
    }
    return currency || '';
  };

  // Get flag for currency
  const getCurrencyFlag = (currency: string) => {
    const country = Object.values(COUNTRIES_INFO).find(
      (countryInfo) => countryInfo.currency === currency
    );
    return country?.flag;
  };

  // Get currency options from API
  const currencyOptions =
    currencies?.map((currency) => {
      const localizedCurrency = localizeCurrency(currency);
      return {
        value: currency,
        label: FormatCurrencyForDisplay(localizedCurrency) as string,
        flag: getCurrencyFlag(currency),
      };
    }) || [];

  // Update payment handle settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: {
      is_collect_customer_name?: boolean;
      is_collect_payment_purpose?: boolean;
      is_email_mandatory?: boolean;
      is_phone_mandatory?: boolean;
      currency?: string;
      payment_methods_list?: number[];
    }) => {
      if (!handle) throw new Error('Handle is required');
      return updatePaymentHandleSettings({
        handle_name: handle,
        ...settings,
        is_email_mandatory: true,
        is_phone_mandatory: true,
      });
    },
    onSuccess: () => {
      // Update original settings to current state (for future error reversion)
      setOriginalSettings({
        mandatoryInfo,
        collectCustomerName,
        collectNotes,
        selectedCurrency,
        paymentMethodsList,
      });

      // Invalidate and refetch config to get latest data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
      });

      notify.success(t('innerLayout.paymePage.settings.updateSuccess'), { maxWidth: '32rem' });
    },
    onError: (error: any) => {
      // Revert to original settings on error
      if (originalSettings) {
        setMandatoryInfo(originalSettings.mandatoryInfo);
        setCollectCustomerName(originalSettings.collectCustomerName);
        setCollectNotes(originalSettings.collectNotes);
        setSelectedCurrency(originalSettings.selectedCurrency);
        setPaymentMethodsList(originalSettings.paymentMethodsList);
      }

      const errorMessage = getPayMeErrorMessage(error, t);

      notify.error(errorMessage, { maxWidth: "32rem" });
    },
  });

  // Helper function to check if settings have changed
  const hasSettingsChanged = () => {
    if (!originalSettings) return false;

    // Compare mandatoryInfo
    if (mandatoryInfo !== originalSettings.mandatoryInfo) {
      return true;
    }

    // Compare checkboxes
    if (collectCustomerName !== originalSettings.collectCustomerName) {
      return true;
    }

    if (collectNotes !== originalSettings.collectNotes) {
      return true;
    }

    if (canViewPaymentIntegrations) {
      // Compare currency
      if (selectedCurrency !== originalSettings.selectedCurrency) {
        return true;
      }

      // Compare payment methods list
      const currentMethods = [...paymentMethodsList].sort().join(',');
      const originalMethods = [...originalSettings.paymentMethodsList]
        .sort()
        .join(',');
      if (currentMethods !== originalMethods) {
        return true;
      }
    }

    return false;
  };

  // Helper function to build settings payload
  const buildSettingsPayload = () => {
    const payload: {
      is_collect_customer_name?: boolean;
      is_collect_payment_purpose?: boolean;
      is_email_mandatory?: boolean;
      is_phone_mandatory?: boolean;
      currency?: string;
      payment_methods_list?: number[];
    } = {};

    // Map mandatoryInfo to API fields
    if (mandatoryInfo === 'mobile') {
      payload.is_phone_mandatory = true;
      payload.is_email_mandatory = false;
    } else if (mandatoryInfo === 'email') {
      payload.is_phone_mandatory = false;
      payload.is_email_mandatory = true;
    } else if (mandatoryInfo === 'both') {
      payload.is_phone_mandatory = true;
      payload.is_email_mandatory = true;
    }

    payload.is_collect_customer_name = collectCustomerName;
    payload.is_collect_payment_purpose = collectNotes;
    payload.currency = selectedCurrency;
    if (canViewPaymentIntegrations) {
      payload.payment_methods_list = paymentMethodsList;
    }

    return payload;
  };

  // Handle settings update
  const handleSettingsUpdate = () => {
    if (!handle) return;
    const payload = buildSettingsPayload();
    updateSettingsMutation.mutate(payload);
  };

  const handleCurrencySelect = (currency: string) => {
    setSelectedCurrency(currency);
    // Clear payment methods when currency changes
    setPaymentMethodsList([]);
  };

  const handlePaymentMethodToggle = (gatewayType: string, enabled: boolean) => {
    if (enabled) {
      // Get the selected integration ID for this gateway type, or use the first available one
      let selectedId = selectedIntegrationIds.get(gatewayType);
      const integrations = availablePaymentMethods.get(gatewayType) || [];
      
      // If no integration ID is selected for this gateway type, use the first available one
      if (!selectedId && integrations.length > 0) {
        selectedId = integrations[0].id;
        // Update selectedIntegrationIds to remember this selection
        setSelectedIntegrationIds((prev) => {
          const updated = new Map(prev);
          updated.set(gatewayType, selectedId!);
          return updated;
        });
      }
      
      if (selectedId) {
        setPaymentMethodsList((prev) => {
          if (!prev.includes(selectedId!)) {
            return [...prev, selectedId!];
          }
          return prev;
        });
      }
    } else {
      // Remove all integration IDs for this gateway type
      const integrations = availablePaymentMethods.get(gatewayType) || [];
      setPaymentMethodsList((prev) =>
        prev.filter((id) => !integrations.some((item) => item.id === id))
      );
    }
  };

  const handleIntegrationIdSelect = (
    gatewayType: string,
    integrationId: number
  ) => {
    setSelectedIntegrationIds((prev) => {
      const updated = new Map(prev);
      updated.set(gatewayType, integrationId);
      return updated;
    });

    // Update payment methods list if this method is already enabled
    setPaymentMethodsList((prev) => {
      const integrations = availablePaymentMethods.get(gatewayType) || [];
      // Remove old integration IDs for this gateway type
      const filtered = prev.filter(
        (id) => !integrations.some((item) => item.id === id)
      );
      // Add the newly selected integration ID
      return [...filtered, integrationId];
    });
  };

  const handleMandatoryInfoChange = (value: 'mobile' | 'email' | 'both') => {
    setMandatoryInfo(value);
    const customerInfoOption = value === 'mobile' ? 'mobile_number' : value === 'email' ? 'email' : 'both_email_and_mobile';
    amplitude.track(PAYME_EVENTS.PAYME_CHECKOUT_INFO_SELECTED, {
      customer_info_option: customerInfoOption,
    });
  };

  const handleCollectCustomerNameChange = (checked: boolean) => {
    setCollectCustomerName(checked);
  };

  const handleCollectNotesChange = (checked: boolean) => {
    setCollectNotes(checked);
  };

  const breadcrumbsLinks = [
    {
      text: t('innerLayout.navbar.payme'),
      active: true,
      onClickHandler: () => navigate(INNER_ROUTES.PAYME),
    },
    {
      text: t('innerLayout.paymePage.settings.title'),
      active: false,
    },
  ];

  return (
    <div className={styles['settings-wrapper']}>
      {/* Header Section */}
      <div className={styles['settings-header']}>
        <Breadcrumbs linksProps={breadcrumbsLinks} />
        <div className={styles['settings-header__content']}>
          <h1 className={styles['settings-header__title']}>
            {t('innerLayout.paymePage.settings.title')}
          </h1>
          <p className={styles['settings-header__description']}>
            {t('innerLayout.paymePage.settings.description')}
          </p>
        </div>
      </div>

      {/* Settings Form */}
      <div className={styles['settings-form']}>
        {/* PayMe link status */}
        {canChangePaymentHandle && (
          <div className={styles['settings-section']}>
            <div className={styles['settings-section__header']}>
              <h2 className={styles['settings-section__title']}>
                {t('innerLayout.paymePage.settings.linkStatus.title')}
              </h2>
            </div>
            <div className={styles['settings-section__content']}>
              <div className={styles['settings-toggle']}>
                <Toggle
                  key={`toggle-${toggleVersion}`}
                  checked={isActive}
                  onChangeHandler={handleToggleChange}
                  size="md"
                  disabled={changeStatusMutation.isPending}
                />
                <span className={styles['settings-toggle__label']}>
                  {isActive
                    ? t('innerLayout.paymePage.settings.linkStatus.active')
                    : t('innerLayout.paymePage.settings.linkStatus.inactive')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Rest of settings with skeleton when activating/deactivating */}
        <div className={styles['settings-form__rest']}>
          {!isActive && <div className={styles['settings-form__overlay']} />}
          {changeStatusMutation.isPending ? (
            <>
              {/* PayMe link info skeleton */}
              <div className={styles['settings-section']}>
                <div className={styles['settings-section__header']}>
                  <SkeletonBase height="1.5rem" width="12rem" />
                </div>
                <div className={styles['settings-section__content']}>
                  <SkeletonBase height="2.5rem" width="100%" />
                </div>
              </div>

              {/* Divider */}
              <div className={styles['settings-divider']}></div>

              {/* Checkout info skeleton */}
              <div className={styles['settings-section']}>
                <div className={styles['settings-section__header']}>
                  <SkeletonBase height="1.5rem" width="10rem" />
                </div>
                <div className={styles['settings-section__content']}>
                  <SkeletonBase height="1.25rem" width="8rem" />
                  <div className={styles['settings-radio-group__options']}>
                    <SkeletonBase height="1.5rem" width="8rem" />
                    <SkeletonBase height="1.5rem" width="8rem" />
                    <SkeletonBase height="1.5rem" width="12rem" />
                  </div>
                  <SkeletonBase height="1.25rem" width="10rem" />
                  <SkeletonBase height="1.5rem" width="10rem" />
                  <SkeletonBase height="1.5rem" width="12rem" />
                </div>
              </div>

              {/* Divider */}
              <div className={styles['settings-divider']}></div>

              {/* Currencies skeleton */}
              <div className={styles['settings-section']}>
                <div className={styles['settings-section__header']}>
                  <SkeletonBase height="1.5rem" width="14rem" />
                </div>
                <div className={styles['settings-section__content']}>
                  <SkeletonBase height="2.5rem" width="100%" />
                </div>
              </div>

              {/* Save button skeleton */}
              <div className={styles['settings-actions']}>
                <SkeletonBase height="2.5rem" width="8rem" />
              </div>
            </>
          ) : (
            <>
              {/* PayMe link info */}
              <div className={styles['settings-section']}>
                <div className={styles['settings-section__header']}>
                  <h2 className={styles['settings-section__title']}>
                    {t('innerLayout.paymePage.settings.linkInfo.title')}
                  </h2>
                </div>
                <div className={styles['settings-section__content']}>
                  <div className={styles['settings-input']}>
                    <Input
                      id="payme-handle-settings"
                      name="payme-handle-settings"
                      value={PAYME_BASE_URL + '/' + handle}
                      readOnly={true}
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className={styles['settings-divider']}></div>

              {/* Checkout info */}
              <div className={styles['settings-section']}>
                <div className={styles['settings-section__header']}>
                  <h2 className={styles['settings-section__title']}>
                    {t('innerLayout.paymePage.settings.checkoutInfo.title')}
                  </h2>
                </div>
                <div className={styles['settings-section__content']}>
                  <div className={styles['settings-radio-group']}>
                    <p className={styles['settings-radio-group__label']}>
                      {t(
                        'innerLayout.paymePage.settings.checkoutInfo.mandatoryLabel'
                      )}
                    </p>
                    {/* <div className={styles['settings-radio-group__options']}>
                      <Checkbox
                        id="mandatory-mobile"
                        type="radio"
                        text={t(
                          'innerLayout.paymePage.settings.checkoutInfo.mobileNumber'
                        )}
                        selected={mandatoryInfo === 'mobile'}
                        onChangeHandler={(checked) => {
                          if (checked) {
                            handleMandatoryInfoChange('mobile');
                          }
                        }}
                      />
                      <Checkbox
                        id="mandatory-email"
                        type="radio"
                        text={t(
                          'innerLayout.paymePage.settings.checkoutInfo.email'
                        )}
                        selected={mandatoryInfo === 'email'}
                        onChangeHandler={(checked) => {
                          if (checked) {
                            handleMandatoryInfoChange('email');
                          }
                        }}
                      />
                      <Checkbox
                        id="mandatory-both"
                        type="radio"
                        text={t(
                          'innerLayout.paymePage.settings.checkoutInfo.both'
                        )}
                        selected={mandatoryInfo === 'both'}
                        onChangeHandler={(checked) => {
                          if (checked) {
                            handleMandatoryInfoChange('both');
                          }
                        }}
                      />
                    </div> */}
                    <p className={styles['settings-radio-group__hint']}>
                      {t(
                        'innerLayout.paymePage.settings.checkoutInfo.bothMandatoryHint'
                      )}
                    </p>
                  </div>
                  <div className={styles['settings-checkboxes']}>
                    <p className={styles['settings-checkboxes__label']}>
                      {t('innerLayout.paymePage.settings.additionalInfo.title')}
                    </p>
                    <Checkbox
                      id="collect-customer-name"
                      text={t(
                        'innerLayout.paymePage.settings.additionalInfo.collectCustomerName'
                      )}
                      checked={collectCustomerName}
                      onChangeHandler={handleCollectCustomerNameChange}
                    />
                    <Checkbox
                      id="collect-notes"
                      text={t(
                        'innerLayout.paymePage.settings.additionalInfo.collectNotes'
                      )}
                      checked={collectNotes}
                      onChangeHandler={handleCollectNotesChange}
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className={styles['settings-divider']}></div>

              {/* Currencies and payment methods */}
              <div className={styles['settings-section']}>
                <div className={styles['settings-section__header']}>
                  <h2 className={styles['settings-section__title']}>
                    {t('innerLayout.paymePage.settings.currencies.title')}
                  </h2>
                </div>
                {canViewPaymentIntegrations ? (
                  <>
                    <p className={styles['settings-section__hint']}>
                      {t(
                        'innerLayout.paymePage.settings.currencies.paymentMethodsHint'
                      )}
                    </p>
                    <div className={styles['settings-section__content']}>
                      <div className={styles['settings-dropdown']}>
                        <Dropdown
                          label={t(
                            'innerLayout.paymePage.settings.currencies.currencyLabel'
                          )}
                          width="full"
                        >
                          <DropdownButton
                            placeholder={t(
                              'innerLayout.paymePage.settings.currencies.currencyPlaceholder'
                            )}
                            text={
                              selectedCurrency
                                ? currencyOptions.find(
                                    (opt) => opt.value === selectedCurrency
                                  )?.label
                                : ''
                            }
                            disabled={
                              !currencies ||
                              isIntegrationsLoading ||
                              updateSettingsMutation.isPending ||
                              !isActive
                            }
                          />
                          <DropdownMenu verticalPlacement='auto'>
                            {currencyOptions.map((option) => (
                              <DropdownMenuItem
                                key={option.value}
                                text={option.label}
                                icon={option.flag}
                                iconType="country"
                                selected={selectedCurrency === option.value}
                                onClickHandler={() =>
                                  handleCurrencySelect(option.value)
                                }
                              />
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                      </div>

                  {/* Payment Methods List */}
                      <PaymentMethodsList
                        selectedCurrency={selectedCurrency}
                        isIntegrationsLoading={
                          isIntegrationsLoading || isIntegrationsNextLoading
                        }
                        availablePaymentMethods={availablePaymentMethods}
                        selectedIntegrationIds={selectedIntegrationIds}
                        paymentMethodsList={paymentMethodsList}
                        isActive={isActive}
                        isUpdatePending={updateSettingsMutation.isPending}
                        isStatusChangePending={changeStatusMutation.isPending}
                        onPaymentMethodToggle={handlePaymentMethodToggle}
                        onIntegrationIdSelect={handleIntegrationIdSelect}
                      />

                      {!isStaff && (
                        <>
                      {/* Divider */}
                          <div className={styles['settings-divider']}></div>

                      {/* Checkout Customization Section */}
                      <div className={styles['settings-checkout-customization']}>
                            <h2 className={styles['settings-section__title']}>
                              {t(
                                'innerLayout.paymePage.settings.checkoutCustomization.title'
                              )}
                            </h2>
                            <p
                              className={
                                styles['settings-checkout-customization__hint']
                              }
                            >
                              {t(
                                'innerLayout.paymePage.settings.checkoutCustomization.hint'
                              )}
                            </p>
                            <Button
                              variant="link"
                              color="primary"
                              size="sm"
                              label={t(
                                'innerLayout.paymePage.settings.checkoutCustomization.link'
                              )}
                              leadingIcon={PaintbrushPencil}
                              onClickHandler={() =>
                                navigate(INNER_ROUTES.CHECKOUT_CUSTOMIZATION)
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <div
                    className={styles['settings-section__permission-hint-row']}
                  >
                    <span
                      className={styles['settings-section__permission-hint-icon']}
                      aria-hidden
                    >
                      {CircleInfo}
                    </span>
                    <p
                      className={
                        styles['settings-section__permission-hint-message']
                      }
                    >
                      {t(
                        'innerLayout.paymePage.settings.currencies.noPaymentIntegrationsPermission'
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className={styles['settings-divider']}></div>

              {/* Save Settings Button */}
              <div className={styles['settings-actions']}>
                <Button
                  onClickHandler={handleSettingsUpdate}
                  disabled={
                    !isActive ||
                    (canViewPaymentIntegrations &&
                      paymentMethodsList.length === 0) ||
                    updateSettingsMutation.isPending ||
                    changeStatusMutation.isPending ||
                    !handle ||
                    !hasSettingsChanged() ||
                    (canViewPaymentIntegrations &&
                      (isIntegrationsLoading || isIntegrationsNextLoading)) ||
                    (canViewPaymentIntegrations &&
                      !!selectedCurrency &&
                      selectedCurrency !== originalSettings?.selectedCurrency &&
                      paymentMethodsList.length === 0)
                  }
                  loading={updateSettingsMutation.isPending}
                  label={t('innerLayout.paymePage.settings.saveButton')}
                  color="primary"
                  variant="contained"
                  size="md"
                />
              </div>
            </>
          )}
        </div>
      </div>     

      {/* Deactivate Confirmation Modal */}
      <DeactivateConfirmModal
        open={showDeactivateConfirm}
        onClose={handleCancelDeactivate}
        onConfirm={handleConfirmDeactivate}
        isBranch={false}
        isLoading={changeStatusMutation.isPending}
      />
    </div>
  );
};
